import React from "react";
import { RouteComponentProps, RouterProps } from "react-router";
import { Box, Button, Card, CardContent, Grid, LinearProgress, Paper } from "@mui/material";
import { Typography } from "@mui/material";
import { DateTime } from "luxon";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { appContext } from "../../../../AppContext";
import IbssSvgIcon from "../../../../Components/Icons/SvgIcon/IbssSvgIcon";
import CalendarIcon from "../../../../Components/Icons/CalendarIcon";
import BuildingOverviewIcon from "../../../../Components/Icons/BuildingOverviewIcon";
import IbssChip from "../../../../Components/Navigation/Chip/IbssChip";
import { TimeIcon } from "@mui/x-date-pickers";
import IbssButton from "../../../../Components/Buttons/Button/IbssButton";
import { BookingParty, Delegates, TicketedEvents } from "./DataModels";
import { IRawPagedResponse, PagedResponse } from "../../../../Providers.Api/Models";
import { getBuildingNameUsingFloorNodeId, getBuildingNodeIdUsingFloorNodeId } from "../../../../Common/Helper";
import { DateHelper } from "../../../../Common/DateHelper";
import EventDetailsSkeleton from "./EventDetailsSkeleton";
import Link from '@mui/material/Link';
import PhoneBoothFilledIcon from "../../../../Components/Icons/PhoneBoothFilledIcon";
import BackArrowIcon from "../../../../Components/Icons/BackArrowIcon";
import CursorIcon from "../../../../Components/Icons/CursorIcon";
import BookingPartiesPicker, { Attendee } from "../../../../Components/DialogLaunchers/BookingPartiesPicker/BookingPartiesPicker";
import Spinner from "../../../../Components/Navigation/LoadingSpinner/Spinner";
import IbssPageHeader from "../../../../Components/Headings/PageHeading/IbssPageHeader";
import { GetAnnouncementDetails } from "../../../../Components/Cards/EventCard/GetAnnouncementDetails";

class EventDetails extends IbssPage<IProps, IState>
{
    private get local() { return appContext().localStorageProvider; }
    private get labels() { return appContext().labels; }
    private buildingId: number;

    constructor(props: IProps)
    {
        super(props);
        this.state =
        {
            isLoading: false,
            bookingId: '',
            eventTitle: '',
            eventShortDescription: '',
            eventLongDescription: '',
            websiteUrl: '',
            phone: '',
            imageUri: '',
            eventStartTime: DateTime.now(),
            eventEndTime: DateTime.now(),
            eventDate: DateTime.now(),
            ticketPrice: 0,
            eventCapacity: 0,
            eventCheckedIn: 0,
            status: '',
            bookingPartyCount: 0,
            isWaitingListAllowed: 0,
            bookingParties: [],
            visitorBookingParties: [],
            isBookForYourSelfDisabled: false,
            disableAddAttendeeButton: false,
            isAttendeesLoading: false,
            eventStatus: null,
            eventPermission: '',
        }
        this.buildingId = parseInt(this.props.match.params.buildingid);
    }
    private announcementHelper = new GetAnnouncementDetails({});

    public componentDidMount(): void
    {
        const { match } = this.props
        this.pageTitle = this.labels.funcEvents_S;
        this.getEventDetails(this.buildingId, match.params.eventId);
    }

    private async getEventDetails(nodeId: number, eventId: string): Promise<void>
    {
        this.setState({ isLoading: true });
        try 
        {
            const data = await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.byEventid.get<IEventDetails>({
                nodeId: nodeId,
                select: TicketedEvents,
                eventid: eventId
            });
            await this.setStateAsync({
                bookingId: data.Booking_Id,
                eventTitle: data.Event_Title,
                eventShortDescription: data.Event_Short_Description,
                eventLongDescription: data.Event_Description,
                websiteUrl: data.WebSite_URL,
                phone: data.Phone,
                imageUri: data.ImageURI,
                status: data.Event_Status,
                eventCapacity: data.Event_Capacity,
                eventCheckedIn: data.IsAttendees_CheckingIn,
                isWaitingListAllowed: data.IsWaitingList_Allowed,
                ticketPrice: data.Ticket_Price,
                eventStartTime: DateHelper.fromIsoByNode(data.Event_Start_DateTime,this.buildingId),
                eventEndTime: DateHelper.fromIsoByNode(data.Event_End_DateTime,this.buildingId),
                eventDate: DateHelper.fromIsoByNode(data.Event_End_DateTime,this.buildingId),
                eventPermission: data.Invite_Permission,
                isLoading: false
            })
            await this.getBookingParties();
        }
        catch (error) 
        {
            this.setState({ isLoading: false });
        }
    }

    private async getBookingParties(): Promise<void> 
    {
        try
        {
            let skipToken = '';
            const allBookingParties: BookingParty[] = [];
            do
            {
                const bookingPartiesResponse = await appContext().ibssApiClientV2.v2.byNodeid.bookingparties.get<PagedResponse<BookingParty[]>>({
                    nodeId: this.buildingId,
                    select: BookingParty,
                    top: 200,
                    filter: `Booking_Id eq ${this.state.bookingId} and Booking_Participant_Status eq '1' and (Booking_Participant_Type eq 1 or Booking_Participant_Type eq 2)`,
                    skipToken: skipToken,
                });

                const bookingParties = bookingPartiesResponse.value;
                skipToken = bookingPartiesResponse.skipToken || '';

                allBookingParties.push(...bookingParties);
            } while (skipToken !== '');

            // Filter participants into visitors,non-visitors and owner
            const isVisitor = (type: number) => type === 2;
            const isNotVisitor = (type: number) => type === 1;
            const isOwner = (type: number) => type === 4;

            const visitors = allBookingParties.filter(party => isVisitor(party.Booking_Participant_Type));
            const nonVisitors = allBookingParties.filter(party => isNotVisitor(party.Booking_Participant_Type));
            const owner = allBookingParties.find(party => isOwner(party.Booking_Participant_Type));
            const existingUser = allBookingParties.find(party => party.Booking_Participant_Email === this.local.getUserDetails().email);

            const currentUserEmail = this.local.getUserDetails().email;
            const isCurrentUserIsOwner = owner?.Booking_Participant_Email === currentUserEmail || existingUser;

            const mapBookingParty = (party: BookingParty, isVisitor: boolean) => ({
                email: party.Booking_Participant_Email,
                name: party.Booking_Participant_Name,
                organisation: party.Booking_Participant_Organisation,
                resourceId: party.Record_Id,
                visitor: isVisitor,
                type: party.Booking_Participant_Type,
                backgroundColor: "",
                visitorTypeId:""
            });

            const currentUser = this.local.getUserDetails();
            const totalCount = visitors.length + nonVisitors.length + (owner ? 1 : 0);

            let isUserAttending = false;
            let eventAttendeesCount = 0;

            if (allBookingParties?.length) {
                const currentBookingParties = allBookingParties.filter(b => b.Booking_Id === this.state.bookingId);
                eventAttendeesCount = currentBookingParties.length;

                isUserAttending = currentBookingParties.some(
                    b => b.Booking_Participant_Email === currentUser.email
                );
            }

            this.setState({
                bookingPartyCount: totalCount,
                bookingParties: nonVisitors.map(party => mapBookingParty(party, false)),
                visitorBookingParties: visitors.map(party => mapBookingParty(party, true)),
                eventStatus: this.announcementHelper.getEventStatus(
                    this.state.eventDate,
                    this.state.isWaitingListAllowed === 1,
                    this.state.eventCapacity,
                    eventAttendeesCount,
                    isUserAttending
                )
            });

            if (totalCount < this.state.eventCapacity)
            {
                // When total participants are less than event capacity
                this.setState({
                    disableAddAttendeeButton: false,
                    isBookForYourSelfDisabled: isCurrentUserIsOwner ? true : false,
                });
            } 
            else if (totalCount >= this.state.eventCapacity && this.state.isWaitingListAllowed === 1)
            {
                // When event is full but waiting list is allowed
                this.setState({
                    disableAddAttendeeButton: false,
                    isBookForYourSelfDisabled: isCurrentUserIsOwner ? true : false,
                });
            } 
            else
            {
                // When event is full and no waiting list
                this.setState({
                    disableAddAttendeeButton: true,
                    isBookForYourSelfDisabled: true,
                });
            }
        } catch (error)
        {
        }        
    }

    private async updateStatusToCompletedBooked (): Promise<void>
    {
        try 
        {           
            await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.byEventid.patch({
                nodeId: this.buildingId,
                eventid: this.props.match.params.eventId,
                body: {
                    Event_Status: "Completed_Booked"
                }
            }) 
        } 
        catch (error) 
        {    
        }
    }


    private async bookForYourSelf(): Promise<void> 
    {
        const currentUser = this.local.getUserDetails();
        this.setState({
            isAttendeesLoading: true,
        });
    
        const payload = [
            {
                Booking_Participant_Email: currentUser.email,
                Booking_Participant_Name: currentUser.displayName,
                Booking_Participant_Organisation: "",
                Booking_Participant_Type: 1,
                IsParticipant_On_WaitingList: this.state.bookingPartyCount < this.state.eventCapacity ? 0 : 1,
                Ticked_Events_Id : this.props.match.params.eventId,
                DisableExtUpdate: true,
            }
        ];
    
        try 
        {
            const data = await appContext().ibssApiClientV2.v2.byNodeid.bookingparties.post({
                nodeId: this.buildingId,
                bookingid: this.state.bookingId,
                body: payload,
            });
    
            if (data) 
            {
                this.setState({
                    isAttendeesLoading: false,
                });
            }
    
            if (this.state.bookingPartyCount === this.state.eventCapacity) 
            {
                this.updateStatusToCompletedBooked ();
            }
    
            this.getEventDetails(this.buildingId, this.props.match.params.eventId);
        } 
        catch (error) 
        {
            this.setState({
                isAttendeesLoading: false,
            });
        }
    }
    
    private async bookingPartiesSubmitted(attendees : Attendee[], visitors: Attendee[]): Promise<void> 
    {
        
        this.setState({ isAttendeesLoading: true });

        // Combine current participants and filter out existing ones
        const completeParticipants = [...attendees , ...visitors];
        const existingEmails = new Set(
            [...this.state.bookingParties, ...this.state.visitorBookingParties].map(
                participant => participant.email
            )
        );
        const newParticipants = completeParticipants.filter(
            participant => !existingEmails.has(participant.email)
        );
    
        // Calculate remaining capacity and split participants
        const remainingCapacity = this.state.eventCapacity - this.state.bookingPartyCount;
        const participants = newParticipants.map((participant, index) => ({
            Booking_Participant_Email: participant.email,
            Booking_Participant_Name: participant.name,
            Booking_Participant_Organisation: participant.organisation,
            Booking_Participant_Type: participant.type,
            IsParticipant_On_WaitingList: index >= remainingCapacity ? 1 : 0,
            Ticked_Events_Id : this.props.match.params.eventId,
            DisableExtUpdate: true,
        }));

        try 
        {
            // Submit participants
            const data = await appContext().ibssApiClientV2.v2.byNodeid.bookingparties.post({
                nodeId: this.buildingId,
                bookingid: this.state.bookingId,
                body: participants,
            });
    
            if (data) 
            {
                this.getBookingParties();
                this.setState({ isAttendeesLoading: false });
            }
    
            // Update status if capacity is reached
            if (this.state.bookingPartyCount === this.state.eventCapacity) 
            {
                this.updateStatusToCompletedBooked ();
            }
        } 
        catch (error) 
        {
            this.setState({ isAttendeesLoading: false });
        }
    }

    public render(): JSX.Element
    {

        if (this.state.isLoading) 
        {
            return <EventDetailsSkeleton />;
        }

        const isActionRestricted = this.state.status === "Completed_Booked" || this.state.status === "Cancelled" || this.state.status === "Completed";
        const hasEventEnded  = this.state.eventEndTime < DateHelper.now(this.buildingId)

        return (
            <Box className="page-height-exct-header">
                <Box className="rightPanel-main-content">
                    {this.state.isAttendeesLoading && <Spinner />}
                    <Box position={"relative"}>
                        <Box
                            component="img"
                            className="flex-home-bg"
                            src={this.state.imageUri}
                            alt="hero banner"
                        />
                        <Box
                            position={"absolute"}
                            component="img"
                            sx={{
                                width: '100%',
                                top: 30,
                                maxWidth: 200,
                                borderRadius: 2,
                                boxShadow: 3,
                                backgroundColor: ' var(--ui-background-alternate)',
                            }}
                            src={this.state.imageUri}
                            alt="avatar"
                        />
                    </Box>
                    <Grid container spacing={4} position={'relative'} top={252}>
                        {/* Left Section */}
                        <Grid item xs={12} md={8} zIndex={'11'} mb={4}>
                            <Paper sx={{ p: 4 }}>
                                <Box display={'flex'} justifyContent={'space-between'}>
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        maxWidth: '100%',
                                        wordBreak: 'break-word', // break long words at any point
                                        whiteSpace: 'normal',  }}>
                                            {this.state.eventTitle}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        maxWidth: '100%',
                                        wordBreak: 'break-word', // break long words at any point
                                        whiteSpace: 'normal',  }}>
                                            {this.state.eventShortDescription}  
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {this.state.eventStatus}
                                    </Box>
                                </Box>
                                <Box display={'flex'} gap={2} my={4}>
                                    <Box display={'flex'} gap={1} mb={1}>
                                        <IbssSvgIcon fontSize="small" sx={{ color: (theme) => theme.palette.text.primary }}>
                                            <BuildingOverviewIcon />
                                        </IbssSvgIcon>
                                        <Typography variant="body2">
                                            {getBuildingNameUsingFloorNodeId(this.buildingId)}
                                        </Typography>
                                    </Box>
                                    <Box display={'flex'} gap={1} mb={1}>
                                        <IbssSvgIcon fontSize="small" >
                                            <CalendarIcon />
                                        </IbssSvgIcon>
                                        <Typography variant="body2">
                                            {this.state.eventDate.toLocaleDateString()}
                                        </Typography>
                                    </Box>
                                    <Box display={'flex'} gap={1}>
                                        <IbssSvgIcon fontSize="small">
                                            <TimeIcon />
                                        </IbssSvgIcon>
                                        <Typography variant="body2">
                                            {this.state.eventStartTime.toLocaleTimeString()} - {this.state.eventEndTime.toLocaleTimeString()}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    {/* Description */}
                                    <Typography sx={{ mt: 2,display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        maxWidth: '100%',
                                        wordBreak: 'break-word', // break long words at any point
                                        whiteSpace: 'normal',  }} variant="body2">
                                        {this.state.eventLongDescription}
                                    </Typography>

                                    {/* Contact Info */}
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2, }}>
                                        <Paper variant="elevation" sx={{bgcolor:'var(--ui-mid-tone)',display: 'flex', alignItems: 'center', p: 1.5, maxWidth: '50%'}}>
                                            <IbssSvgIcon sx={{ fontSize: 12, mr: 1 }}><CursorIcon /></IbssSvgIcon>   
                                               <Link href={this.state.websiteUrl} sx={{whiteSpace: 'nowrap',overflow: 'hidden',textOverflow: 'ellipsis'}} variant="body2" target="_blank"> {this.state.websiteUrl}</Link>
                                        </Paper>
                                        <Paper variant="elevation" sx={{bgcolor:'var(--ui-mid-tone)',display: 'flex', alignItems: 'center', p: 1.5}}><IbssSvgIcon sx={{ fontSize: 12, mr: 1 }}><PhoneBoothFilledIcon /></IbssSvgIcon>{this.state.phone}</Paper>
                                    </Box>
                                </Box>
                                <Box mt={4}>
                                    <Link href="#" variant="body2" sx={{ display: 'flex', gap: 2, mt: 2, p: 2,textDecoration: 'none' }} onClick={() => this.props.history.goBack()}>
                                        <Box display={'flex'} alignItems={'center'} gap={1}>
                                            <IbssSvgIcon fontSize="small">
                                                <BackArrowIcon />
                                            </IbssSvgIcon>
                                            {this.labels.funcBackToEvents_S}
                                        </Box>
                                    </Link>
                                </Box>
                            </Paper>

                        </Grid>

                        {/* Right Section */}
                        <Grid item xs={12} md={4} zIndex={'11'}>
                            <Card sx={{ p: 2 }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {this.labels.funcBookASession_S}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                                        <Typography>{this.labels.funcAdvancedBookingAvailability_S}</Typography>
                                        <Typography sx={{ ml: 'auto' }}>{`${this.state.bookingPartyCount > this.state.eventCapacity ? this.state.eventCapacity : this.state.bookingPartyCount} / ${this.state.eventCapacity}`}</Typography>
                                    </Box>
                                    <LinearProgress variant="determinate" value={Math.min((this.state.bookingPartyCount / this.state.eventCapacity) * 100, 100)} sx={{ mt: 1 }} />
                                    <Typography sx={{ mt: 2, fontWeight: 'bold' }}>{this.labels.HubLabelRetailPrice}: {this.state.ticketPrice}</Typography>
                                    <Button fullWidth variant="contained"
                                        disabled={this.state.isBookForYourSelfDisabled || isActionRestricted || hasEventEnded}
                                        onClick={() => this.bookForYourSelf()} color="primary" sx={{ mt: 2 }}>
                                        {this.labels.funcBookForYourself_S}
                                    </Button>
                                    <BookingPartiesPicker
                                        buildingId={Number(getBuildingNodeIdUsingFloorNodeId(this.buildingId))}
                                        bookingParties={this.state.bookingParties}
                                        bookingPartyVisitors={this.state.visitorBookingParties}
                                        onSubmit={(attendees,visitors) => this.bookingPartiesSubmitted(attendees,visitors)}
                                        hideExistingAttendees={true}
                                        disabled={(this.state.disableAddAttendeeButton && !isActionRestricted) || hasEventEnded}      
                                        disableVisitorParties={this.state.eventPermission === "Internal"}  
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    }
}

interface IProps extends RouteComponentProps<IQueryParams>
{

}

export interface IQueryParams
{
    buildingid: string;
    eventId: string;
}

interface IState
{
    isLoading: boolean;
    isAttendeesLoading: boolean;
    bookingId: string;
    eventTitle: string;
    eventShortDescription: string;
    eventLongDescription: string;
    websiteUrl: string;
    phone: string;
    imageUri: string;
    eventStartTime: DateTime;
    eventEndTime: DateTime;
    eventDate: DateTime;
    ticketPrice: number;
    status: string;
    eventCapacity: number;
    eventCheckedIn: number;
    bookingPartyCount: number;
    isWaitingListAllowed: number;
    visitorBookingParties: Attendee[];
    bookingParties: Attendee[];
    isBookForYourSelfDisabled: boolean;
    disableAddAttendeeButton: boolean;
    eventStatus: React.ReactNode;
    eventPermission: string;
}

export interface ICallToAction
{
    id: string;
    title: string;
    description: string;
    buttonLabel: string;
    src: string;
    imageAlt: string;
    route: string;
    permissions: boolean;
}

interface IEventDetails 
{
    Booking_Id: string;
    Node_Id: number;
    Event_Title: string;
    Event_Short_Description: string;
    Event_Description: string;
    WebSite_URL: string;
    Phone: string;
    ImageURI: string;
    Event_Date: string;
    Event_Start_DateTime: string;
    Event_End_DateTime: string;
    Event_Capacity: number;
    Ticket_Price: number;
    IsWaitingList_Allowed: number;
    IsAttendees_CheckingIn: number;
    Event_Status: string;
    Invite_Permission: string;
}

export default EventDetails;